'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { allowsVendorDestinations } from '@extern/consent/state';
import { useConsent } from '@extern/consent/use-consent';
import Script from 'next/script';
import { getFirstPartyVisitorId } from '@/lib/analytics/first-party-visitor-id';
import { LEGACY_IDENTIFY_TRAIT_KEYS } from '@/lib/analytics/legacy-traits';
import { SEGMENT_ANONYMOUS_ID_PARAM, SEGMENT_IDENTITY_ADOPTED_FLAG } from '@/lib/analytics/segment-anonymous-id';
import { getEnv } from '@/utils/env';

/**
 * App-only override — the marketing site talks to the stock Segment hosts. Repeated in both load
 * shapes below because `integrations` replaces the whole map rather than merging into it.
 */
const SEGMENT_IO_OPTIONS = {
  apiHost: 'segment-api.extern.com/v1',
  protocol: 'https'
} as const;

/**
 * Segment always loads, consent or not: `Segment.io` feeds the warehouse
 * (`data-platform-417014`), which is the first-party carve-out that must keep working for 100% of
 * traffic. What consent decides is whether the *device-mode destinations* (Reddit Pixel, GA4-web,
 * AdWords, LinkedIn Insight, Appcues) come with it. `{ All: false }` stops their bundles from
 * being fetched at all, verified in our own HAR captures — not merely from receiving events.
 *
 * The gate is binary (`statistics || marketing`) rather than per-destination, matching the Webflow
 * head: granular gating needs the exact integration names from the Segment panel, and a wrong name
 * there fails silently.
 */
function getLoadOptions(allowVendors: boolean, appcues: boolean) {
  if (allowVendors) {
    return {
      integrations: {
        Appcues: appcues,
        'Segment.io': SEGMENT_IO_OPTIONS
      }
    };
  }
  return {
    integrations: {
      All: false,
      'Segment.io': SEGMENT_IO_OPTIONS
    }
  };
}

/**
 * `appcues` gates the Appcues device-mode destination, which Segment loads on our behalf.
 *
 * Appcues is not referenced anywhere in this codebase: it arrives purely as a Segment destination, so
 * the ONLY place it can be switched off from the app is right here in the load options. On the public
 * Labs preview an Appcues product tour firing mid-lab would be the single most "this is an app I never
 * signed up for" event available, and it is a third-party script running for a visitor who has
 * consented to nothing. Consent decides whether destinations load at all; this decides whether
 * Appcues is among them on routes where consent says yes.
 */
export function SegmentScript(t0) {
  const $ = _c(11);
  const {
    appcues: t1
  } = t0;
  const appcues = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getEnv("SEGMENT_WRITE_KEY");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const writeKey = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = JSON.stringify(LEGACY_IDENTIFY_TRAIT_KEYS);
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const legacyIdentifyTraitKeys = t3;
  const {
    categories,
    isResolved
  } = useConsent();
  const [visitorId, setVisitorId] = useState(null);
  const allowVendors = allowsVendorDestinations(categories);
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setVisitorId(getFirstPartyVisitorId());
    };
    t5 = [];
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  if (!visitorId) {
    return null;
  }
  let t6;
  if ($[4] !== visitorId) {
    t6 = JSON.stringify(visitorId);
    $[4] = visitorId;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = `
      (function() {

        // Scrub legacy identify traits from Segment's cache before the SDK loads,
        // so analytics.js does not replay stale values (e.g. progress %) into Mixpanel.
        try {
          var LEGACY_KEYS = ${legacyIdentifyTraitKeys};
          var TRAITS_KEY = 'ajs_user_traits';
          var rawTraits = window.localStorage.getItem(TRAITS_KEY);
          if (rawTraits) {
            var traits = JSON.parse(rawTraits);
            if (traits && typeof traits === 'object') {
              var changed = false;
              for (var i = 0; i < LEGACY_KEYS.length; i++) {
                var legacyKey = LEGACY_KEYS[i];
                if (Object.prototype.hasOwnProperty.call(traits, legacyKey)) {
                  delete traits[legacyKey];
                  changed = true;
                }
              }
              if (changed) {
                window.localStorage.setItem(TRAITS_KEY, JSON.stringify(traits));
              }
            }
          }
        } catch (e) {
          // localStorage / JSON can throw; continue loading Segment either way
        }

        // define the key where the global analytics object will be accessible
        // customers can safely set this to be something else if need be
        var globalAnalyticsKey = "analytics"

        // Create a queue, but don't obliterate an existing one!
        var analytics = window[globalAnalyticsKey] = window[globalAnalyticsKey] || [];

        // If the real analytics.js is already on the page return.
        if (analytics.initialize) return;

        // If the snippet was invoked already show an error.
        if (analytics.invoked) {
          if (window.console && console.error) {
            console.error("Segment snippet included twice.");
          }
          return;
        }

        // Invoked flag, to make sure the snippet
        // is never invoked twice.
        analytics.invoked = true;

        // A list of the methods in Analytics.js to stub.
        analytics.methods = [
          "trackSubmit",
          "trackClick",
          "trackLink",
          "trackForm",
          "pageview",
          "identify",
          "reset",
          "group",
          "track",
          "ready",
          "alias",
          "debug",
          "page",
          "screen",
          "once",
          "off",
          "on",
          "addSourceMiddleware",
          "addIntegrationMiddleware",
          "setAnonymousId",
          "addDestinationMiddleware",
          "register"
        ];

        analytics.factory = function(e) {
          return function() {
            if (window[globalAnalyticsKey].initialized) {
              // Sometimes users assigned analytics to a variable before analytics is done loading, resulting in a stale reference.
              // If so, proxy any calls to the 'real' analytics instance.
              return window[globalAnalyticsKey][e].apply(window[globalAnalyticsKey], arguments);
            }
            var args = Array.prototype.slice.call(arguments);

            // Add buffered page context object so page information is always up-to-date
            if (["track", "screen", "alias", "group", "page", "identify"].indexOf(e) > -1) {
              var c = document.querySelector("link[rel='canonical']");
              args.push({
                __t: "bpc",
                c: c && c.getAttribute("href") || undefined,
                p: location.pathname,
                u: location.href,
                s: location.search,
                t: document.title,
                r: document.referrer
              });
            }

            args.unshift(e);
            analytics.push(args);
            return analytics;
          };
        };

        // For each of our methods, generate a queueing stub.
        for (var i = 0; i < analytics.methods.length; i++) {
          var key = analytics.methods[i];
          analytics[key] = analytics.factory(key);
        }

        // Define a method to load Analytics.js from our CDN,
        // and that will be sure to only ever load it once.
        analytics.load = function(key, options) {
          // Create an async script element based on your key.
          var t = document.createElement("script");
          t.type = "text/javascript";
          t.async = true;
          t.setAttribute("data-global-segment-analytics-key", globalAnalyticsKey)
          t.src = "https://segment-analytics.extern.com/analytics.js/v1/" + key + "/analytics.min.js";

          // Insert our script next to the first script element.
          var first = document.getElementsByTagName("script")[0];
          first.parentNode.insertBefore(t, first);
          analytics._loadOptions = options;
        };

        analytics._writeKey = "${writeKey}";

        // Add a version to keep track of what's in the wild.
        analytics.SNIPPET_VERSION = "5.2.1";

        // Segment must inherit our first-party id rather than mint its own — a visitor who
        // rejects consent (and so never loads Segment's own storage-backed id logic) must still
        // resolve to the same anonymousId as one who accepts. Queued via the stub above and
        // flushed once analytics.min.js loads, same as any other call — must run before .load()
        // so it lands ahead of any track/page/identify already queued this tick.
        analytics.setAnonymousId(${t6});

        // Adopt the anonymous id extern-website forwarded, so a sign-up that
        // started there stays one person in Mixpanel. localStorage is per
        // origin, so extern.com and app.extern.com otherwise roll their own and
        // the platform's identify merges only its own half.
        //
        // This has to happen HERE and nowhere else. <PageView /> sits above the
        // route tree in app-providers, so its page call — the one Mixpanel
        // renders as "Viewed Sign Up" — fires before any effect inside the
        // callback route could run. The stub queues setAnonymousId ahead of
        // everything buffered, which is the only ordering that covers it.
        //
        // reset() FIRST, and it is not optional. setAnonymousId only replaces
        // the anonymous id; a persisted ajs_user_id from whoever used this
        // browser before would survive, so the page view would ship as that
        // older account while carrying the website's anonymous id — and the
        // identify that follows would then bind the same anonymous id to the
        // NEW account. Mixpanel would merge two real people into one profile.
        // Adopting without resetting is worse than not adopting at all.
        //
        // Discarding the stored identity is safe precisely here: the parameter
        // is only ever present on the hand-off from extern-website, which by
        // construction carries an account that was just created. reset() rolls
        // its own anonymous id, so the adoption has to come after it.
        //
        // Segment is all this can reset. PostHog persists its person too, but
        // posthog.init has not run yet at snippet time, so discardStaleIdentity
        // forgets that one before the first identify of this page load.
        //
        // Wrapped because Segment is the one tag that loads for 100% of
        // traffic: a malformed query string must not be able to take it down.
        try {
          var forwardedAnonymousId = new URLSearchParams(location.search).get("${SEGMENT_ANONYMOUS_ID_PARAM}");
          if (forwardedAnonymousId) {
            analytics.reset();
            analytics.setAnonymousId(forwardedAnonymousId);
            // Both calls above are queued, so storage still shows the previous
            // occupant until analytics.js arrives. This flag tells
            // discardStaleIdentity not to read that and reset Segment a second
            // time — which would flush after the adoption and discard the id.
            window.${SEGMENT_IDENTITY_ADOPTED_FLAG} = true;
          }
        } catch (e) {
          // Continue loading Segment; a lost correlation beats a lost tag.
        }

        // Stamp context.consent on every event. Segment's own enforcement is FAIL-OPEN: an event
        // carrying no consent object flows to ALL destinations, including the server-side CAPIs
        // that no browser-side gate can reach. Read from Cookiebot at event time rather than from
        // React state, so an event queued before the answer is still stamped with the answer that
        // was current when it was flushed, and coerced with !! so a missing CMP reads as denied.
        analytics.addSourceMiddleware(function (params) {
          // Prefer the answer @extern/consent has RESOLVED over Cookiebot's own object. They are the
          // same thing except for a visitor whose answer was given on the other host of this site,
          // where this window's Cookiebot has nothing and the resolved answer is the mirror. Reading
          // Cookiebot alone stamped those events as a denial and kept the destinations this stamp
          // exists to gate switched off (Bugbot on #3024). Still read at flush time, not from React,
          // and still coerced so anything missing reads as denied.
          var c = window.__externConsent || (window.Cookiebot && window.Cookiebot.consent) || {};
          params.payload.obj.context = params.payload.obj.context || {};
          params.payload.obj.context.consent = {
            categoryPreferences: {
              necessary: true,
              preferences: !!c.preferences,
              statistics: !!c.statistics,
              marketing: !!c.marketing
            }
          };
          params.next(params.payload);
        });

        analytics.addSourceMiddleware(function (params) {
          function getGAClientId() {
            var gaCookie = document.cookie.split('; ').find(function (row) { return row.startsWith('_ga='); });

            if (gaCookie) {
              var matches = gaCookie.match(/GA1.[0-9]+.([^;]+)/);
              if (matches) {
                return matches[1];
              }
            }

            return null;
          }

          if (['track', 'page', 'identify'].indexOf(params.payload.obj.type) > -1) {
            params.payload.obj.properties = Object.assign({}, params.payload.obj.properties, {
              ga_client_id: getGAClientId()
            });
          }

          params.next(params.payload);
        });
      })();
    `;
  let t8;
  if ($[6] !== t7) {
    t8 = <Script id="segment-script">{t7}</Script>;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = isResolved ? <Script id="segment-load">{`
        (function() {
          var analytics = window.analytics;

          if (!analytics || analytics.initialize || window.__externSegmentLoaded) {
            return;
          }

          window.__externSegmentLoaded = true;
          analytics.load("${writeKey}", ${JSON.stringify(getLoadOptions(allowVendors, appcues))});
        })();
      `}</Script> : null;
  let t10;
  if ($[8] !== t8 || $[9] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[8] = t8;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  return t10;
}